import { useState } from "react"
import { router } from "@inertiajs/react"
import { Archive, Bot, ExternalLink, Image, Lock, MoreHorizontal, RotateCcw, Trash2, X } from "lucide-react"
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { cn } from "@/lib/utils"
import { DeleteClipDialog } from "./DeleteClipDialog"
import type { Clip } from "./types"

// Shared actions dropdown for clip cards / list rows.
// `tone` controls trigger styling:
//   - "ghost" (default): plain icon button for use inside list rows
//   - "overlay": semi-transparent dark pill for use atop a card's thumbnail
export function ClipActionsMenu({
  clip,
  tone = "ghost",
  className,
  onRequestThumbnails,
}: {
  clip: Clip
  tone?: "ghost" | "overlay"
  className?: string
  onRequestThumbnails?: (clipId: number) => void
}) {
  const [deleteOpen, setDeleteOpen] = useState(false)
  const hasThumbs = clip.has_thumbnails
  const isDeprecated = clip.status === "deprecated"
  const isMissing = clip.status === "missing"
  const isQueued = clip.status === "queued_for_enrichment"
  const isNeedsProcessing = clip.status === "needs_processing"
  const showOneTimeUseToggle = !isDeprecated && !isMissing

  function triggerThumbnails() {
    if (onRequestThumbnails) {
      onRequestThumbnails(clip.id)
    } else {
      router.post(`/clips/${clip.id}/extract_thumbnails`, {}, { preserveScroll: true })
    }
  }

  return (
    <>
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Clip actions"
          onClick={(e) => e.stopPropagation()}
          className={cn(
            "inline-flex h-7 w-7 cursor-pointer items-center justify-center rounded-md transition-colors",
            tone === "overlay"
              ? "bg-page/90 text-ink-display shadow-sm hover:bg-page"
              : "text-ink-muted hover:bg-surface hover:text-ink-display",
            className,
          )}
        >
          <MoreHorizontal className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56" onClick={(e) => e.stopPropagation()}>
        {!hasThumbs ? (
          <DropdownMenuItem onSelect={triggerThumbnails}>
            <Image /> Get thumbnail & enrich
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem onSelect={triggerThumbnails}>
            <Image /> Refresh thumbnails
          </DropdownMenuItem>
        )}

        {hasThumbs && isNeedsProcessing && (
          <DropdownMenuItem onSelect={() => router.patch(`/clips/${clip.id}/mark_for_enrichment`, {}, { preserveScroll: true })}>
            <Bot /> Queue for enrichment
          </DropdownMenuItem>
        )}

        {isQueued && (
          <DropdownMenuItem onSelect={() => router.patch(`/clips/${clip.id}/cancel_enrichment`, {}, { preserveScroll: true })}>
            <X /> Cancel enrichment
          </DropdownMenuItem>
        )}

        {clip.dropbox_web_url && (
          <DropdownMenuItem asChild>
            <a
              href={clip.dropbox_web_url}
              target="_blank"
              rel="noopener noreferrer"
              className="no-underline"
            >
              <ExternalLink /> View in Dropbox
            </a>
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        {showOneTimeUseToggle && (clip.one_time_use ? (
          <DropdownMenuItem onSelect={() => router.patch(`/clips/${clip.id}/unmark_one_time_use`, {}, { preserveScroll: true })}>
            <RotateCcw /> Unmark one-time-use
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem onSelect={() => router.patch(`/clips/${clip.id}/mark_one_time_use`, {}, { preserveScroll: true })}>
            <Lock /> Mark as one-time-use
          </DropdownMenuItem>
        ))}
        {isDeprecated ? (
          <DropdownMenuItem onSelect={() => router.patch(`/clips/${clip.id}/undeprecate`, {}, { preserveScroll: true })}>
            <RotateCcw /> Un-deprecate
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem destructive onSelect={() => router.patch(`/clips/${clip.id}/deprecate`, {}, { preserveScroll: true })}>
            <Archive /> Deprecate
          </DropdownMenuItem>
        )}
        <DropdownMenuItem destructive onSelect={() => setDeleteOpen(true)}>
          <Trash2 /> Delete clip
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
    <DeleteClipDialog clip={clip} open={deleteOpen} onOpenChange={setDeleteOpen} />
    </>
  )
}
